'use client'

import Link from 'next/link'
import Mountains from '@/components/common/animated-icons/Mountains'

interface SearchEmptyStateProps {
  query: string
  categories: { slug: string; label: string }[]
  onClear: () => void
  onBrowse: (slug: string) => void
}

export function SearchEmptyState({ query, categories, onClear, onBrowse }: SearchEmptyStateProps) {
  const term = query.trim()

  return (
    <section className="rounded-lg border border-primary-ink-10 bg-white px-6 py-12 text-center sm:px-10 sm:py-16">
      <div aria-hidden="true" className="mx-auto flex h-[70px] w-[100px] items-center justify-center">
        <Mountains />
      </div>

      <h2 className="mt-6 break-words text-2xl font-medium leading-8 tracking-[-0.5px] text-primary-ink-100">
        We don&rsquo;t list &ldquo;{term}&rdquo; yet
      </h2>
      <p className="mx-auto mt-3 max-w-md text-base leading-6 tracking-[-0.3px] text-primary-ink-60">
        Tell us what you need and which environment it&rsquo;s for. Our licensing team will check whether we can source
        it and follow up by email.
      </p>

      <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link
          href={`/marketplace/request-product?request=${encodeURIComponent(term)}`}
          className="flex h-10 max-w-full items-center justify-center rounded-md bg-primary-ink-100 px-5 text-sm font-medium tracking-[-0.25px] text-white transition-colors hover:bg-primary-ink-85 motion-reduce:transition-none">
          <span className="truncate">Request &ldquo;{term}&rdquo;</span>
        </Link>
        <button
          type="button"
          onClick={onClear}
          className="flex h-10 items-center justify-center rounded-md border border-primary-ink-30 bg-white px-5 text-sm font-medium tracking-[-0.25px] text-primary-ink-100 transition-colors hover:border-primary-ink-60 motion-reduce:transition-none">
          Clear search
        </button>
      </div>

      <div className="mx-auto mt-10 max-w-xl border-t border-primary-ink-10 pt-6">
        <p className="text-sm text-primary-ink-60">Or browse a category</p>
        <ul className="mt-3 flex flex-wrap justify-center gap-2">
          {categories.map((category) => (
            <li key={category.slug}>
              <button
                type="button"
                onClick={() => onBrowse(category.slug)}
                className="h-8 rounded-full border border-primary-ink-10 bg-white px-3 text-sm text-primary-ink-100 transition-colors hover:border-primary-ink-30 motion-reduce:transition-none">
                {category.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
